/**
 * VisibilityControls — per-field public/connections/private selector inside the
 * profile edit flow (Phase A, AC-04.1).
 *
 * Surfaces an explicit save-error state so a failed save is never silently
 * dropped (AC-03.3). Gated by `social.profile_card`.
 */

import { useEffect, useState } from 'react';
import {
  getFieldVisibility,
  updateFieldVisibility,
  getMyProfile,
  updateProfile,
} from '../../services/api/profileService';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import type { FieldVisibility, ProfileCardField } from '../../types/social';
import { PEEK_VISIBILITY_OPTIONS, PeekVisibility } from '../../types/profile';
import './VisibilityControls.css';

const TIERS: { value: FieldVisibility; label: string }[] = [
  { value: 'public', label: 'Public' },
  { value: 'connections', label: 'Connections' },
  { value: 'private', label: 'Private' },
];

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function VisibilityControls() {
  const enabled = useFeatureFlag('social.profile_card');
  const [fields, setFields] = useState<ProfileCardField[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [error, setError] = useState<string | null>(null);

  // Peek-visibility (V2 Amethyst WU-02, R5) — a single global setting, not a
  // per-field tier, so it's persisted through the plain profile-update path
  // (PUT /api/profile), not the field-visibility batch endpoint above.
  const [peekVisibility, setPeekVisibility] = useState<PeekVisibility>('initials-only');
  const [peekSaveState, setPeekSaveState] = useState<SaveState>('idle');

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    getFieldVisibility()
      .then((f) => {
        if (!cancelled) setFields(f);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    getMyProfile().then((profile) => {
      if (!cancelled && profile?.peekVisibility) setPeekVisibility(profile.peekVisibility);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  if (!enabled) return null;

  const setPeek = async (visibility: PeekVisibility) => {
    const previous = peekVisibility;
    setPeekVisibility(visibility);
    setPeekSaveState('saving');
    try {
      await updateProfile({ peekVisibility: visibility });
      setPeekSaveState('saved');
    } catch {
      setPeekVisibility(previous);
      setPeekSaveState('error');
    }
  };

  const setTier = async (fieldKey: string, visibility: FieldVisibility) => {
    const previous = fields;
    const next = fields.map((f) => (f.key === fieldKey ? { ...f, visibility } : f));
    setFields(next);
    setSaveState('saving');
    setError(null);
    try {
      await updateFieldVisibility([{ fieldKey, visibility }]);
      setSaveState('saved');
    } catch (e) {
      // Roll back the optimistic change and surface the failure explicitly.
      setFields(previous);
      setSaveState('error');
      setError(e instanceof Error ? e.message : 'Failed to save visibility');
    }
  };

  return (
    <section className="visibility-controls">
      <header className="visibility-controls__header">
        <h4>Room-card visibility</h4>
        {peekSaveState === 'saving' && <span className="visibility-controls__status">Saving…</span>}
        {peekSaveState === 'saved' && (
          <span className="visibility-controls__status visibility-controls__status--ok">Saved</span>
        )}
        {peekSaveState === 'error' && (
          <span className="visibility-controls__status visibility-controls__status--error">Save failed</span>
        )}
      </header>
      <p className="visibility-controls__peek-hint">
        Who sees your photo and first name on room cards and peek sheets, before they open your profile.
      </p>
      <div className="visibility-controls__peek-tiers" role="radiogroup" aria-label="Room-card visibility">
        {PEEK_VISIBILITY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={peekVisibility === opt.value}
            className={`visibility-controls__peek-tier ${peekVisibility === opt.value ? 'is-active' : ''}`}
            onClick={() => setPeek(opt.value)}
          >
            <span className="visibility-controls__peek-tier-label">{opt.label}</span>
            <span className="visibility-controls__peek-tier-hint">{opt.hint}</span>
          </button>
        ))}
      </div>

      <header className="visibility-controls__header visibility-controls__header--fields">
        <h4>Who can see each field</h4>
        {saveState === 'saving' && <span className="visibility-controls__status">Saving…</span>}
        {saveState === 'saved' && (
          <span className="visibility-controls__status visibility-controls__status--ok">Saved</span>
        )}
        {saveState === 'error' && (
          <span className="visibility-controls__status visibility-controls__status--error">
            {error ?? 'Save failed'}
          </span>
        )}
      </header>

      {loading ? (
        <p className="visibility-controls__loading">Loading…</p>
      ) : (
        <ul className="visibility-controls__list">
          {fields.map((f) => (
            <li className="visibility-controls__row" key={f.key}>
              <span className="visibility-controls__label">{f.label}</span>
              <div className="visibility-controls__tiers" role="radiogroup" aria-label={f.label}>
                {TIERS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    role="radio"
                    aria-checked={f.visibility === t.value}
                    className={`visibility-controls__tier ${
                      f.visibility === t.value ? 'is-active' : ''
                    }`}
                    onClick={() => setTier(f.key, t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default VisibilityControls;
